/**
 * The answer — `mockups/studio/money.html` §the answer, the region at the top.
 *
 * Same strip as Tasks / Clients: one answer line, then equal columns of caption +
 * figure. **Net cash** spends the page’s one `.hero-figure`; past due and the Wise
 * queue are `.sub-figure` — the two things on this page somebody has to *do*
 * something about.
 *
 * What the mockup prints that no field carries, and is therefore not printed here:
 *
 *   - **"up 4.5% on July"** and **"90.1% is cash"** — two percentages over two
 *     periods. `recognised_vs_collected` has the amounts and no ratio, and a page
 *     that divided two of them would be computing. The amounts themselves are all
 *     printed: recognised and collected open the sentence.
 *   - **"Vela is €1,780 across two invoices ($1,940 at the internal rate)"** — a
 *     per-client rollup of past due. `analytics_receivables` gives the total and the
 *     invoices, and summing two of them by client is arithmetic this page does not do.
 *   - **"$5,353 and €1,842.60 unmatched"** — the queue's totals per currency, which is
 *     the same problem again.
 *
 * The report asks for each. What replaces them is the same fact stated from what is
 * there: the total, the count, and the oldest — `daysBetween` is `dates.ts`'s, and the
 * clock is `integrations/clock.ts` `now()`.
 */

import { daysBetween, type MoneyOutView, type StudioMoneyView } from '@fareast/shared'

import { now } from '../../../integrations/clock.js'
import { Region, RegionCols } from '../../../components/portal/Region.js'
import {
  Answer,
  B,
  HeroFigure,
  More,
  Muted,
  Num,
  Small,
  SubFigure,
} from '../../../components/portal/Text.js'
import { HeroCapInfo } from '../../../components/studio/HeroCapInfo.js'
import { useFormat } from '../../../format/useFormat.js'
import type { NameOf } from './names.js'
import { EXPENSE_KIND_WORDS } from './state.js'

/** As much of `analytics_receivables` as this region reads. */
export type Receivables = {
  readonly total: MoneyOutView['total']
  readonly invoices: readonly {
    readonly ref: string
    readonly client: string
    readonly days_past_due: number
  }[]
}

export type AnswerRegionProps = {
  readonly period: string
  readonly out: MoneyOutView
  readonly queue: StudioMoneyView['wise_queue']
  readonly receivables: Receivables | null
  /**
   * The six-month table, for this period's own row.
   *
   * `mockups/studio/money.html`'s opening sentence names **both** figures, and both
   * are on the wire: `MoneyOutView.collected` is what arrived, and
   * `recognised_vs_collected` carries what was invoiced for the same month. Opening
   * on the collected figure alone dropped half of the sentence's own comparison —
   * money invoiced against money that turned up is the point of the section under it.
   */
  readonly months: StudioMoneyView['recognised_vs_collected']
  readonly nameOf: NameOf
}

export function AnswerRegion({
  period,
  out,
  queue,
  receivables,
  months,
  nameOf,
}: AnswerRegionProps) {
  const format = useFormat()
  const thisMonth = months.find((month) => month.period === period) ?? null
  const clients = new Set((receivables?.invoices ?? []).map((one) => one.client))
  const oldestDays = oldestQueueDays(queue)
  const outKinds = kindSentence(out)
  const pastDueTip =
    receivables === null || receivables.invoices.length === 0
      ? 'Open invoices past their due date, across every client. Nothing is past due.'
      : `Open invoices past their due date. ${pastDueSentence(receivables, clients.size, nameOf)}`
  const wiseTip =
    queue.length === 0
      ? 'Bank credits that arrived and have not been matched to an invoice yet. Every credit has been placed.'
      : oldestDays === null
        ? 'Bank credits that arrived and have not been matched to an invoice yet. None of it is allocated to an invoice.'
        : `Bank credits waiting on a person to match them. The oldest has been waiting ${String(oldestDays)} ${oldestDays === 1 ? 'day' : 'days'}.`

  return (
    <div className="studio-hero studio-hero--n3">
      <Region>
        <Answer>
          {`${format.monthLong(period)} `}
          {thisMonth === null ? null : (
            <>
              {'recognised '}
              <B>
                <Num value={thisMonth.recognised} />
              </B>
              {' and '}
            </>
          )}
          {'collected '}
          <B>
            <Num value={out.collected} />
          </B>
          {' against '}
          <B>
            <Num value={out.total} />
          </B>
          {' paid out.'}
        </Answer>

        <RegionCols>
          <div>
            <HeroCapInfo
              label="Net cash"
              tip={`Collected minus everything paid out in ${format.monthLong(period)}. Out of what came in, ${format.money(out.total)} went back out: ${outKinds}.`}
            />
            <HeroFigure value={out.net_cash} />
            <Small>
              <Muted>
                <More href="#out">The ledger →</More>
              </Muted>
            </Small>
          </div>

          <div>
            <HeroCapInfo label="Past due" tip={pastDueTip} />
            {receivables === null ? (
              <SubFigure value="—" />
            ) : (
              <SubFigure value={receivables.total} bad />
            )}
            {receivables === null || receivables.invoices.length === 0 ? null : (
              <Small>
                <Muted>
                  {String(clients.size)} {clients.size === 1 ? 'client' : 'clients'},{' '}
                  {String(receivables.invoices.length)}{' '}
                  {receivables.invoices.length === 1 ? 'invoice' : 'invoices'}
                </Muted>
              </Small>
            )}
          </div>

          <div>
            <HeroCapInfo label="Wise queue" tip={wiseTip} />
            <SubFigure value={queue.length} />
            <Small>
              <Muted>
                <More href="#wise">The queue →</More>
              </Muted>
            </Small>
          </div>
        </RegionCols>
      </Region>
    </div>
  )
}

/** "two clients, three invoices, the oldest of them 47 days on FES-2026-039". */
export function pastDueSentence(
  receivables: Receivables,
  clientCount: number,
  nameOf: NameOf,
): string {
  const invoices = [...receivables.invoices].sort((a, b) => b.days_past_due - a.days_past_due)
  const oldest = invoices[0]
  const head = `${String(clientCount)} ${clientCount === 1 ? 'client' : 'clients'}, ${String(invoices.length)} ${invoices.length === 1 ? 'invoice' : 'invoices'}`
  if (oldest === undefined) return `${head}.`
  return `${head}. The oldest is ${nameOf(oldest.client)}’s ${oldest.ref}, ${String(oldest.days_past_due)} days past its due date.`
}

/** "three contractors, eight subscriptions, the rails and the draw", from the counts. */
export function kindSentence(out: MoneyOutView): string {
  const parts = out.by_kind.map((kind) => {
    const count = out.lines.filter((line) => line.kind === kind.kind).length
    const words = EXPENSE_KIND_WORDS[kind.kind]
    return `${String(count)} ${count === 1 ? words.one : words.other}`
  })

  return parts.length === 0 ? 'nothing' : parts.join(', ')
}

/**
 * How long the oldest credit in the queue has been waiting, in whole days.
 *
 * `daysBetween` is `packages/shared/src/dates.ts`'s and `now()` is
 * `integrations/clock.ts`'s — the two rules that make this a fact rather than a
 * `new Date()` in a component.
 */
export function oldestQueueDays(queue: StudioMoneyView['wise_queue']): number | null {
  const received = queue.map((one) => one.received_at).sort()
  const oldest = received[0]
  if (oldest === undefined) return null
  return daysBetween(new Date(oldest), now())
}
